import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { AppButton } from "components/common/buttons";
import { AppPasswordField } from "components/common/inputs";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { changePassword } from "modules/systems/services/user.service";
import {
  getPasswordStrengthColor,
  getPasswordStrengthText,
  validatePassword,
} from "utils/passwordPolicy";

const cardSx = {
  border: "1px solid #e3e9ef",
  borderRadius: "12px",
  boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
};

const SecurityTab = ({ user }) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleInputChange = (field) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChangePassword = async () => {
    try {
      setLoading(true);

      // Validate inputs
      if (!formData.currentPassword.trim()) {
        setSnackbar({
          open: true,
          message: "Vui lòng nhập mật khẩu hiện tại",
          severity: "error",
        });
        return;
      }

      if (!formData.newPassword.trim()) {
        setSnackbar({
          open: true,
          message: "Vui lòng nhập mật khẩu mới",
          severity: "error",
        });
        return;
      }

      if (!formData.confirmPassword.trim()) {
        setSnackbar({
          open: true,
          message: "Vui lòng nhập lại mật khẩu mới",
          severity: "error",
        });
        return;
      }

      // Validate new password strength
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) {
        setSnackbar({
          open: true,
          message: passwordError,
          severity: "error",
        });
        return;
      }

      // Check password confirmation
      if (formData.newPassword !== formData.confirmPassword) {
        setSnackbar({
          open: true,
          message: "Mật khẩu mới và xác nhận mật khẩu không khớp",
          severity: "error",
        });
        return;
      }

      // Check if new password is different from current
      if (formData.currentPassword === formData.newPassword) {
        setSnackbar({
          open: true,
          message: "Mật khẩu mới phải khác mật khẩu hiện tại",
          severity: "error",
        });
        return;
      }

      // Call API to change password
      await changePassword(formData.currentPassword, formData.newPassword);

      setSnackbar({
        open: true,
        message: "Đổi mật khẩu thành công",
        severity: "success",
      });

      // Clear form
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error changing password:", error);

      // Handle specific error messages
      let errorMessage = "Có lỗi xảy ra khi đổi mật khẩu";
      if (error.response?.status === 400) {
        errorMessage = "Mật khẩu hiện tại không đúng";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ justifyItems: "center" }}>
      <Card variant="outlined" sx={cardSx}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">Đổi mật khẩu</Typography>
          </Box>

          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <AppPasswordField
                label="Mật khẩu hiện tại"
                value={formData.currentPassword}
                onChange={handleInputChange("currentPassword")}
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <AppPasswordField
                label="Mật khẩu mới"
                value={formData.newPassword}
                onChange={handleInputChange("newPassword")}
                required
              />
              {formData.newPassword && (
                <Typography
                  variant="body2"
                  color={
                    getPasswordStrengthColor(formData.newPassword) === "success"
                      ? "success.main"
                      : getPasswordStrengthColor(formData.newPassword) ===
                        "warning"
                        ? "warning.main"
                        : "error.main"
                  }
                  sx={{ mt: 1, fontFamily: "Inter, sans-serif" }}
                >
                  Độ mạnh: {getPasswordStrengthText(formData.newPassword)}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <AppPasswordField
                label="Nhập lại mật khẩu mới"
                value={formData.confirmPassword}
                onChange={handleInputChange("confirmPassword")}
                required
                error={
                  formData.confirmPassword &&
                  formData.newPassword !== formData.confirmPassword
                }
                helperText={
                  formData.confirmPassword &&
                    formData.newPassword !== formData.confirmPassword
                    ? "Mật khẩu không khớp"
                    : ""
                }
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontFamily: "Inter, sans-serif", fontSize: "13px" }}
            >
              Yêu cầu mật khẩu:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mt: 0, mb: 3 }}>
              <Typography
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Inter, sans-serif", fontSize: "13px" }}
              >
                Ít nhất 6 ký tự
              </Typography>
              <Typography
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Inter, sans-serif" }}
              >
                Chứa ít nhất 1 chữ thường
              </Typography>
              <Typography
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Inter, sans-serif" }}
              >
                Chứa ít nhất 1 chữ hoa
              </Typography>
              <Typography
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Inter, sans-serif" }}
              >
                Chứa ít nhất 1 số
              </Typography>
            </Box>
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <AppButton
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleChangePassword}
              disabled={
                loading ||
                !formData.currentPassword ||
                !formData.newPassword ||
                !formData.confirmPassword ||
                formData.newPassword !== formData.confirmPassword
              }
            >
              {loading ? "Đang đổi..." : "Đổi mật khẩu"}
            </AppButton>
          </Box>
        </CardContent>
      </Card>

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default SecurityTab;
